// "Where's my order?" — sections/vpx-track.liquid.
// Search by order number + email or by tracking number → the vorynex-track Worker (or sample data while `demo` is
// on) → one normalised result:
//   { found, order, tracking, carrier, service, country, localCarrier: { name, url } | null, stage 1–5,
//     events: [{ ts, text }] newest first, items: [{ title, image, url, howTo, variantId, qty }] }
// Rendered as: headline + ETA (with calendar file) + meta + actions, the 3D route (ported from the v3 reference),
// five steps, history, "while you wait" (order lookups only) or the delivered moment with one-click reorder.
// Below, a sticky scroll scene crossfades four stills with a slow living zoom. Motion only runs while on screen.
const root = document.querySelector('[data-vpx-track]');
if (root) init(root);

// ---------- living dust ----------
// A small canvas of warm dust motes drifting up through the floor's LED light. Brighter near the light line,
// faster and brighter while the visitor scrolls (scroll speed feeds `boost`), calm when they stop. Runs only while
// on screen and the tab is visible; nothing at all under reduced motion. `line()` returns the light line's y in
// CSS px; mode 'fountain' instead throws motes up from a point (the opened box) and lets them settle.
let scrollBoost = 0, lastScrollY = window.scrollY;
addEventListener('scroll', () => {
  const y = window.scrollY;
  scrollBoost = Math.min(4, scrollBoost + Math.abs(y - lastScrollY) / 60);
  lastScrollY = y;
}, { passive: true });

function makeDust(canvas, opts) {
  const ctx = canvas.getContext('2d');
  const dpr = Math.min(1.5, window.devicePixelRatio || 1);
  const count = opts.count || (matchMedia('(max-width: 760px)').matches ? 38 : 70);
  let w = 0, h = 0, raf = 0, on = false, t0 = performance.now(), boost = 0;
  const ps = [];
  const size = () => {
    const r = canvas.getBoundingClientRect();
    w = r.width; h = r.height;
    canvas.width = Math.max(1, Math.round(w * dpr)); canvas.height = Math.max(1, Math.round(h * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  const spawn = (p, first) => {
    if (opts.mode === 'fountain') {
      const o = opts.origin();
      p.x = o.x + (Math.random() - 0.5) * w * 0.06; p.y = o.y;
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 1.5, sp = 0.6 + Math.random() * 2.4;
      p.vx = Math.cos(a) * sp; p.vy = Math.sin(a) * sp; p.life = 0; p.max = 90 + Math.random() * 160;
    } else {
      const ly = opts.line();
      p.x = Math.random() * w;
      p.y = first ? Math.random() * h : ly + Math.random() * h * 0.35;
      p.vx = (Math.random() - 0.5) * 0.12; p.vy = -(0.08 + Math.random() * 0.32);
    }
    p.r = 0.5 + Math.random() * (opts.mode === 'fountain' ? 2.2 : 1.6);
    p.a = 0.25 + Math.random() * 0.6; p.ph = Math.random() * 6.28;
  };
  for (let i = 0; i < count; i++) { const p = {}; spawn(p, true); ps.push(p); }
  const frame = (now) => {
    raf = 0;
    if (!on) return;
    boost = Math.max(boost * 0.93, scrollBoost); scrollBoost *= 0.85;
    const t = (now - t0) / 1000;
    ctx.clearRect(0, 0, w, h);
    const ly = opts.mode === 'fountain' ? opts.origin().y : opts.line();
    const speed = 1 + boost * 1.6;
    for (const p of ps) {
      if (opts.mode === 'fountain') {
        p.life++; p.vy += 0.018; p.vx *= 0.995;
        p.x += p.vx * 0.6; p.y += p.vy * 0.6;
        if (p.life > p.max || p.y > h + 10) spawn(p);
      } else {
        p.x += p.vx + Math.sin(t * 0.7 + p.ph) * 0.12; p.y += p.vy * speed;
        if (p.y < -8 || p.x < -8 || p.x > w + 8) spawn(p);
      }
      const near = Math.exp(-Math.pow((p.y - ly) / (h * 0.16), 2));
      const fade = opts.mode === 'fountain' ? Math.max(0, 1 - p.life / p.max) : 1;
      const al = p.a * (0.25 + 0.75 * near) * (0.7 + 0.3 * Math.min(1, boost)) * fade;
      ctx.fillStyle = 'rgba(255,' + Math.round(200 - 60 * near) + ',' + Math.round(160 - 90 * near) + ',' + al.toFixed(3) + ')';
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill();
    }
    if (opts.onFrame) opts.onFrame(t, boost);
    raf = requestAnimationFrame(frame);
  };
  const start = () => { if (on) return; on = true; size(); raf = requestAnimationFrame(frame); };
  const stop = () => { on = false; if (raf) cancelAnimationFrame(raf); raf = 0; };
  new ResizeObserver(() => { if (on) size(); }).observe(canvas);
  const vis = new IntersectionObserver(([e]) => (e.isIntersecting && !document.hidden ? start() : stop()), { rootMargin: '60px' });
  vis.observe(canvas);
  document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else { const r = canvas.getBoundingClientRect(); if (r.bottom > 0 && r.top < innerHeight) start(); } });
  return { stop, burst() { ps.forEach((p) => spawn(p)); } };
}

// Where a horizontal line at `frac` of an image's height lands in its box with object-fit: cover, object-position
// x% 50%, under a CSS scale `k` about the centre.
function coverLineY(img, box, frac, k) {
  const iw = img.naturalWidth || 2048, ih = img.naturalHeight || 1144;
  const s = Math.max(box.width / iw, box.height / ih);
  const rh = ih * s;
  const y = (box.height - rh) / 2 + frac * rh;
  return box.height / 2 + (y - box.height / 2) * (k || 1);
}

function init(root) {
  const cfg = JSON.parse(root.querySelector('[data-vpx-track-config]').textContent);
  const S = cfg.strings;
  // The t filter HTML-escapes the strings (' -> &#39;) and they are escaped again when rendered, so decode them once here.
  const dec = document.createElement('textarea');
  for (const k in S) if (typeof S[k] === 'string' && S[k].indexOf('&') > -1) { dec.innerHTML = S[k]; S[k] = dec.value; }
  const lang = (cfg.locale || document.documentElement.lang || 'el').slice(0, 2);
  const rm = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const q = (s, el = root) => el.querySelector(s);
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const tx = (k, map) => { let s = S[k] || ''; for (const key in map || {}) s = s.split(key).join(map[key]); return s; };
  const mail = '<a href="mailto:' + esc(cfg.email) + '">' + esc(cfg.email) + '</a>';
  const LAST_KEY = 'vpx-track-last';

  // ---------- on-screen tracking: .is-live only while visible ----------
  const live = new IntersectionObserver((entries) => {
    entries.forEach((e) => e.target.classList.toggle('is-live', e.isIntersecting && !rm));
  }, { rootMargin: '80px' });

  // ---------- hero: the floor video on phones (skipped for Save-Data / reduced motion) ----------
  const hero = q('.vpx-tr-hero');
  live.observe(hero);
  const video = q('.vpx-tr-hero__video');
  const conn = navigator.connection || {};
  if (video && !rm && !conn.saveData && matchMedia('(max-width: 760px)').matches) {
    new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        if (!video.src) { video.src = video.dataset.src; }
        video.play().catch(() => {});
      } else if (!video.paused) video.pause();
    }).observe(video);
  }

  const heroDust = q('[data-vpx-track-dust="hero"]');
  if (heroDust && !rm) {
    const poster = q('.vpx-tr-hero__poster');
    makeDust(heroDust, { line: () => {
      const b = hero.getBoundingClientRect();
      return getComputedStyle(poster).display === 'none' ? b.height * 0.52 : coverLineY(poster, b, 0.5, 1.08);
    } });
  }

  // ---------- form ----------
  const form = q('[data-vpx-track-form]');
  const errEl = q('[data-vpx-track-error]');
  const submitBtn = q('button[type="submit"]', form);
  // Order lookup can be switched off in the section (tracking number only); then the order fields are not rendered.
  let mode = form.querySelector('[data-fields="order"]') ? 'order' : 'track';
  const setMode = (m) => {
    mode = m;
    form.querySelectorAll('.vpx-tr-tab').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === m)));
    form.querySelectorAll('[data-fields]').forEach((f) => { f.hidden = f.dataset.fields !== m; });
    errEl.hidden = true;
  };
  form.addEventListener('click', (e) => {
    const tab = e.target.closest('.vpx-tr-tab');
    if (tab) setMode(tab.dataset.mode);
  });
  const showError = (msg) => { errEl.textContent = msg; errEl.hidden = false; };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    // The order fields exist only when order lookup is switched on in the section.
    const order = form.order ? form.order.value.trim() : '';
    const email = form.email ? form.email.value.trim() : '';
    const tracking = form.track.value.replace(/\s+/g, '').toUpperCase();
    if (mode === 'order') {
      if (!order) return showError(S.err_order);
      if (!/^\S+@\S+\.\S+$/.test(email)) return showError(S.err_email);
    } else if (!tracking) return showError(S.err_track);
    // An order number typed into the tracking field (#1005 / 1005): move it to the order tab and ask for the email,
    // or, without order lookup, explain which number is needed.
    if (mode === 'track' && /^#?\d{3,10}$/.test(tracking)) {
      if (!form.order) return showError(S.hint_order_track);
      setMode('order'); form.order.value = '#' + tracking.replace('#', '');
      form.email.focus(); return showError(S.hint_order);
    }
    errEl.hidden = true;
    search(mode === 'order' ? { mode, order: order.replace(/^#?/, '#'), email } : { mode, tracking });
  });

  // Remember only the tracking number (never the email), for a one-tap check next time.
  const lastBtn = q('[data-vpx-track-last]');
  // Stored for the next visit only: showing it now would push the result down mid-read.
  const remember = (n) => { try { localStorage.setItem(LAST_KEY, n); } catch (e) { /* private mode */ } };
  const showLast = (n) => {
    if (!n) return;
    lastBtn.innerHTML = esc(S.last_search) + ': <b>' + esc(n) + '</b>';
    lastBtn.hidden = false;
    lastBtn.onclick = () => { setMode('track'); form.track.value = n; search({ mode: 'track', tracking: n }); };
  };
  try { showLast(localStorage.getItem(LAST_KEY)); } catch (e) { /* ignore */ }

  q('[data-vpx-track-focus]') && q('[data-vpx-track-focus]').addEventListener('click', (e) => {
    e.preventDefault();
    hero.scrollIntoView({ behavior: rm ? 'auto' : 'smooth', block: 'start' });
    setTimeout(() => (mode === 'order' ? form.order : form.track).focus({ preventScroll: true }), rm ? 0 : 500);
  });

  // ---------- data ----------
  const params = new URLSearchParams(location.search);
  const forcedStage = Math.min(5, Math.max(0, parseInt(params.get('demo') || '0', 10) || 0));

  async function lookup(query) {
    if (cfg.demo || forcedStage || !cfg.api) return demoResult(query);
    const res = await fetch(cfg.api.replace(/\/$/, '') + '/track', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({ locale: lang }, query))
    });
    if (res.status === 404) return { found: false };
    if (!res.ok) throw new Error('track ' + res.status);
    return res.json();
  }

  async function demoResult(query) {
    let stage = forcedStage || 3;
    if (!forcedStage && query.mode === 'track') {
      const d = (query.tracking.match(/(\d)\D*$/) || [])[1];
      if (d) stage = (Number(d) % 5) + 1;
    }
    if (/NOTFOUND|^#?0+$/.test(query.tracking || query.order || '')) return new Promise((r) => setTimeout(() => r({ found: false }), 700));
    // A real product photo for the sample "while you wait" card.
    let demoImg = '';
    try {
      const p = await (await fetch(cfg.root.replace(/\/?$/, '/') + 'products/creatine-monohydrate-500g-1.js')).json();
      demoImg = p.featured_image ? p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=160' : '';
    } catch (e) { /* sample card without a photo */ }
    const now = Date.now(), H = 3600e3, D = 24 * H;
    const evAt = [now - 4 * D - 3 * H, now - 3 * D - 6 * H, now - 2 * D - 2 * H, now - 22 * H, now - 3 * H, now - 1 * H];
    const evStage = [1, 2, 3, 3, 4, 5];
    const events = evAt.map((ts, i) => ({ ts: new Date(ts).toISOString(), text: S['demo_ev_' + (i + 1)], k: evStage[i] }))
      .filter((e) => e.k <= stage).reverse();
    return new Promise((r) => setTimeout(() => r({
      found: true, demo: true,
      gift: params.get('gift') ? { from: 'Γιώργος', to: 'Μαρία', link: cfg.root.replace(/\/?$/, '/') + 'pages/build-your-stack' } : null,
      order: query.mode === 'order' ? query.order : null,
      tracking: query.tracking || 'LF496155594DE',
      carrier: 'DHL', service: 'Kleinpaket', country: (params.get('country') || 'GR').toUpperCase(),
      localCarrier: stage >= 3 ? { name: lang === 'el' ? 'ΕΛΤΑ' : 'ELTA', url: 'https://www.elta.gr/' } : null,
      stage, events,
      items: query.mode === 'order' ? [{ title: S.demo_item, image: demoImg, url: cfg.root.replace(/\/$/, '') + '/products/creatine-monohydrate-500g-1', howTo: S.demo_howto, variantId: 59013129371993, qty: 1 }] : []
    }), 900));
  }

  // ---------- rendering ----------
  const panel = q('[data-vpx-track-panel]');
  const info = q('[data-vpx-track-info]');
  const detail = q('[data-vpx-track-detail]');
  const resultSec = q('[data-vpx-track-result]');
  const idleInfo = info.innerHTML;
  let current = null, currentStage = 0;
  live.observe(panel);

  // Only the latest search may render: a slow earlier answer (e.g. the first order lookup) must not overwrite a newer one.
  let searchSeq = 0;
  async function search(query) {
    const seq = ++searchSeq;
    clearInterval(clockTimer); clearInterval(refreshTimer); // a new search ends the live refresh of the previous one
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.textContent = S.loading + '…';
    // The result is rendered first and scrolled to afterwards: growing content while it was already in view
    // pushed everything below it (layout shift). The skeleton only shows when the panel is already on screen.
    const pr = panel.getBoundingClientRect();
    if (pr.top < innerHeight && pr.bottom > 0) { panel.style.minHeight = pr.height + 'px'; renderLoading(); }
    try {
      const r = await lookup(query);
      if (seq !== searchSeq) return;
      panel.classList.add('is-result', 'vpx-tr-glass'); // set by renderLoading when it runs
      if (!r || !r.found) renderNotFound(query.mode);
      else { renderResult(r); if (r.tracking) remember(r.tracking); startLive(query, r); }
    } catch (e) {
      if (seq === searchSeq) { panel.classList.add('is-result', 'vpx-tr-glass'); renderError(); }
    } finally {
      if (seq === searchSeq) {
        submitBtn.removeAttribute('aria-busy'); submitBtn.textContent = S.submit; panel.style.minHeight = '';
        resultSec.scrollIntoView({ behavior: rm ? 'auto' : 'smooth', block: 'start' });
      }
    }
  }

  function renderLoading() {
    panel.classList.add('is-result', 'vpx-tr-glass');
    info.innerHTML = '<p class="vpx-tr-eyebrow"><span aria-hidden="true"></span>' + esc(S.loading) + '</p>' +
      '<div class="vpx-tr-skel" style="height:28px;width:70%"></div><div class="vpx-tr-skel" style="width:92%"></div>' +
      '<div class="vpx-tr-skel" style="width:76%"></div><div class="vpx-tr-skel" style="width:84%"></div>';
    detail.innerHTML = '';
    drawRoute(0);
  }
  function renderNotFound(m) {
    // A tracking-number search has no email to check, so it gets its own hint.
    info.innerHTML = '<h2 class="vpx-tr-h2">' + esc(S.nf_title) + '</h2><p class="vpx-tr-muted">' + tx(m === 'track' ? 'nf_text_track' : 'nf_text', { __EMAIL__: mail }) + '</p>';
    detail.innerHTML = '';
    drawRoute(0);
    focusHead();
  }
  function renderError() {
    info.innerHTML = '<h2 class="vpx-tr-h2">' + esc(S.err_generic) + '</h2>';
    detail.innerHTML = '';
    drawRoute(0);
    focusHead();
  }
  function focusHead() {
    const h = q('.vpx-tr-h2', info);
    if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
  }

  const dateFmt = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const dayFmt = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' });
  const ICON = {
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="1.5"/><path d="M5 15V5a1 1 0 0 1 1-1h10"/></svg>',
    out: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>',
    cal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="1.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>',
    play: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>',
    gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="3.5" y="9" width="17" height="11.5" rx="1"/><path d="M2.5 9h19v-3h-19zM12 6v14.5M12 6c-1.5-3-5.5-3.5-5.5-1s4 1 5.5 1c1.5 0 5.5 1.5 5.5-1S13.5 3 12 6z"/></svg>',
    share: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="18" cy="5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="19" r="2.5"/><path d="M8.2 10.8l7.6-4.5M8.2 13.2l7.6 4.5"/></svg>'
  };

  function renderResult(r, silent) {
    const stage = Math.min(5, Math.max(1, r.stage || 1));
    const last = r.events && r.events[0];
    const eta = estimate(r, stage);
    const dhlUrl = 'https://www.dhl.de/' + (lang === 'de' ? 'de' : 'en') + '/privatkunden/pakete-empfangen/verfolgen.html?piececode=' + encodeURIComponent(r.tracking || '');
    const carrier = (r.carrier || 'DHL') + (r.localCarrier ? ' → ' + r.localCarrier.name : '');

    let etaHtml = '';
    if (stage === 5) {
      etaHtml = '<div class="vpx-tr-eta"><span class="vpx-tr-eta__label">' + esc(S.eta_label) + '</span>' +
        '<span class="vpx-tr-eta__count">✓</span><span class="vpx-tr-eta__date">' + esc(S.eta_delivered) + (last ? ' · ' + esc(dayFmt.format(new Date(last.ts))) : '') + '</span></div>';
    } else if (eta) {
      // Big figure: days until the earliest date; when the window opens today, "≤ N days" until the latest.
      const today = startOfDay(new Date());
      const dFrom = Math.max(0, Math.round((eta.from - today) / 864e5));
      const dTo = Math.max(0, Math.round((eta.to - today) / 864e5));
      const days = dFrom || dTo;
      const unit = (days === 1 ? S.eta_in_one : tx('eta_in_other', { __N__: days })).replace(/^\D*\d+\s*/, '');
      const range = sameDay(eta.from, eta.to) ? dayFmt.format(eta.from) : dayFmt.format(eta.from) + ' – ' + dayFmt.format(eta.to);
      etaHtml = '<div class="vpx-tr-eta"><span class="vpx-tr-eta__label">' + esc(S.eta_label) + '</span>' +
        (days === 0
          ? '<span class="vpx-tr-eta__count">' + esc(S.eta_today) + '</span>'
          : '<span class="vpx-tr-eta__count" data-count="' + days + '"' + (dFrom === 0 ? ' data-prefix="≤ "' : '') + '>0<small>' + esc(unit) + '</small></span>') +
        '<span class="vpx-tr-eta__date">' + esc(range) + '</span><p class="vpx-tr-eta__note">' + esc(S.eta_note) + '</p></div>';
    }

    const actions = [];
    if (r.tracking) actions.push('<button type="button" class="vpx-tr-ghost" data-act="copy">' + ICON.copy + '<span>' + esc(S.copy) + '</span></button>');
    if (r.tracking && stage >= 2) actions.push('<a class="vpx-tr-ghost" href="' + esc(dhlUrl) + '" target="_blank" rel="noopener">' + ICON.out + esc(S.dhl_btn) + '</a>');
    if (r.localCarrier && r.localCarrier.url) actions.push('<a class="vpx-tr-ghost" href="' + esc(r.localCarrier.url) + '" target="_blank" rel="noopener">' + ICON.out + esc(tx('local_btn', { __CARRIER__: r.localCarrier.name })) + '</a>');
    if (eta && stage < 5) actions.push('<button type="button" class="vpx-tr-ghost" data-act="ics">' + ICON.cal + esc(S.calendar) + '</button>');
    if (r.tracking) actions.push('<button type="button" class="vpx-tr-ghost" data-act="share">' + ICON.share + esc(S.share) + '</button>');
    if ((r.events || []).length > 1 && !rm) actions.push('<button type="button" class="vpx-tr-ghost" data-act="replay" aria-pressed="false">' + ICON.play + '<span>' + esc(S.replay) + '</span></button>');

    info.innerHTML =
      '<p class="vpx-tr-eyebrow"><span aria-hidden="true"></span>' + esc(S.status_eyebrow) + (r.demo ? ' <span class="vpx-tr-chip">' + esc(S.sample_chip) + '</span>' : '') +
        (stage < 5 && !r.demo ? ' <span class="vpx-tr-livechip"><i aria-hidden="true"></i>' + esc(S.live_chip) + ' · <span data-checked>' + esc(S.checked_now) + '</span></span>' : '') + '</p>' +
      '<h2 class="vpx-tr-h2">' + esc(S['head_' + stage]) + '</h2>' + etaHtml +
      '<dl class="vpx-tr-meta">' +
        (r.order ? '<div><dt>' + esc(S.meta_order) + '</dt><dd>' + esc(r.order) + '</dd></div>' : '') +
        '<div><dt>' + esc(S.meta_carrier) + '</dt><dd>' + esc(carrier) + '</dd></div>' +
        (r.tracking ? '<div><dt>' + esc(S.meta_track) + '</dt><dd class="is-mono">' + esc(r.tracking) + '</dd></div>' : '') +
        (last ? '<div><dt>' + esc(S.meta_updated) + '</dt><dd>' + esc(dateFmt.format(new Date(last.ts))) + '</dd></div>' : '') +
      '</dl><div class="vpx-tr-actions">' + actions.join('') + '</div>';

    // steps + history (+ stalled notice)
    const steps = [1, 2, 3, 4, 5].map((k) => {
      const done = k < stage || (k === 5 && stage === 5), now = k === stage && stage < 5;
      return '<li class="vpx-tr-step' + (done ? ' is-done' : now ? ' is-now' : '') + '" style="--i:' + (k - 1) + '"' + (now ? ' aria-current="step"' : '') + '>' +
        '<div class="vpx-tr-step-head"><span class="vpx-tr-dot"></span><span class="vpx-tr-line"></span></div>' +
        '<div class="vpx-tr-step-body"><span class="vpx-tr-mono vpx-tr-muted">0' + k + '</span><span class="vpx-tr-step-name">' + esc(S['step_' + k]) + '</span>' +
        '<span class="vpx-tr-step-state">' + esc(done ? S.st_done : now ? S.st_now : S.st_wait) + '</span></div></li>';
    }).join('');
    const hist = (r.events || []).map((e) => '<div class="vpx-tr-ev"><time datetime="' + esc(e.ts) + '">' + esc(dateFmt.format(new Date(e.ts))) + '</time><span>' + esc(e.text) + '</span></div>').join('');
    const stalled = stage === 3 && last && Date.now() - new Date(last.ts) > 4 * 864e5;

    let extra = '';
    const items = r.items || [];
    if (stage === 5) {
      // The delivered moment: the opened box bursts (the owner's photograph, live dust on top). No product is
      // composited into the scene (it read as fake); what they bought is shown honestly below it, with reorder and
      // review, for order lookups only.
      const first = items[0];
      extra = '<div class="vpx-tr-delivered">' +
        '<div class="vpx-tr-dl-scene">' +
          '<picture><source media="(max-width: 760px)" srcset="' + esc(cfg.burstTall) + '"><img src="' + esc(cfg.burst) + '" alt="" loading="lazy"></picture>' +
          '<div class="vpx-tr-glowline vpx-tr-glowline--pulse vpx-tr-dl-line" aria-hidden="true"><span class="vpx-tr-glowline__spot"></span><span class="vpx-tr-glowline__reflect"></span></div>' +
          '<canvas class="vpx-tr-dust" data-dl-dust aria-hidden="true"></canvas>' +
        '</div>' +
        '<div class="vpx-tr-dl-copy"><h3 class="vpx-tr-h2">' + esc(S.delivered_title) + '</h3>' +
        (first ? '<p class="vpx-tr-muted" style="color:#C9C9CE">' + esc(S.delivered_text) + '</p>' +
          '<p class="vpx-tr-eyebrow" style="margin:6px 0 0"><span aria-hidden="true"></span>' + esc(S.inbox_title) + '</p>' +
          '<div class="vpx-tr-inbox">' + items.map((it) => '<a class="vpx-tr-inbox__item" href="' + esc(it.url || '#') + '">' +
            (it.image ? '<img src="' + esc(it.image) + '" alt="" width="56" height="56" loading="lazy">' : '') +
            '<span>' + esc(it.title) + (it.qty > 1 ? ' × ' + it.qty : '') + '</span></a>').join('') + '</div>' +
          '<div class="vpx-tr-actions"><button type="button" class="vpx-tr-btn" data-act="reorder" data-vpx-add' + (first.image ? ' data-vpx-fly-img="' + esc(first.image) + '"' : '') + '>' + esc(S.reorder) + '</button>' +
          (first.url ? '<a class="vpx-tr-ghost" href="' + esc(first.url) + '#judgeme_product_reviews">' + esc(S.review) + '</a>' : '') + '</div>' : '') +
        '</div></div>';
    } else if (items.length) {
      extra = '<div><p class="vpx-tr-eyebrow"><span aria-hidden="true"></span>' + esc(S.wait_title) + '</p><p class="vpx-tr-muted" style="margin-bottom:14px">' + esc(S.wait_text) + '</p>' +
        '<div class="vpx-tr-cards">' + items.map((it) => '<div class="vpx-tr-card">' +
          (it.image ? '<img src="' + esc(it.image) + '" alt="" loading="lazy" width="72" height="72">' : '<img alt="" width="72" height="72">') +
          '<div><h4>' + esc(it.title) + '</h4><p>' + esc(it.howTo || '') + '</p></div></div>').join('') + '</div></div>';
    }

    detail.innerHTML = '<div style="display:flex;flex-direction:column;gap:28px">' +
      '<ol class="vpx-tr-steps' + (silent ? '' : ' is-fill') + '" aria-label="' + esc(S.status_eyebrow) + '">' + steps + '</ol>' +
      '<div class="vpx-tr-map" data-vpx-track-map></div>' +
      (stalled ? '<p class="vpx-tr-notice">' + tx('stalled', { __EMAIL__: mail }) + '</p>' : '') +
      (hist ? '<div class="vpx-tr-history"><span class="vpx-tr-mono vpx-tr-muted" style="padding-bottom:12px">' + esc(S.history) + '</span>' + hist + '</div>' : '') +
      extra + '</div>';

    // wire actions
    root.querySelectorAll('[data-act]').forEach((b) => b.addEventListener('click', () => act(b, r, eta)));
    const del = q('.vpx-tr-delivered', detail);
    if (del && !rm) requestAnimationFrame(() => del.classList.add('is-in'));
    countUp(q('[data-count]', info));
    drawRoute(stage);
    current = r; currentStage = stage;
    buildMap(q('[data-vpx-track-map]', detail), r, stage);
    const dl = q('.vpx-tr-delivered', detail); if (dl) live.observe(dl);
    const dlDust = q('[data-dl-dust]', detail);
    if (dlDust && !rm) {
      const scene = q('.vpx-tr-dl-scene', detail);
      makeDust(dlDust, { mode: 'fountain', count: 60, origin: () => {
        const b = scene.getBoundingClientRect(), mob = matchMedia('(max-width: 760px)').matches;
        return { x: b.width * (mob ? 0.5 : 0.62), y: b.height * (mob ? 0.48 : 0.625) };
      } });
    }
    if (!silent) focusHead();
  }

  // ---------- live: while the page is open and the parcel is on its way, check again every 15 minutes ----------
  // (the Worker caches each parcel for 15 minutes, so this never adds DHL calls beyond one per parcel per window)
  let liveQuery = null, liveSig = '', checkedAt = 0, clockTimer = 0, refreshTimer = 0;
  const sig = (r) => (r.stage || 0) + '|' + ((r.events && r.events[0] && r.events[0].ts) || '');
  function tickChecked() {
    const el = q('[data-checked]', info); if (!el) return;
    const min = Math.floor((Date.now() - checkedAt) / 60000);
    el.textContent = min < 1 ? S.checked_now : min === 1 ? S.checked_one : tx('checked_ago', { __N__: min });
  }
  function startLive(query, r) {
    clearInterval(clockTimer); clearInterval(refreshTimer);
    liveQuery = query; liveSig = sig(r); checkedAt = Date.now();
    if (r.demo || (r.stage || 0) >= 5) return;
    clockTimer = setInterval(tickChecked, 30000);
    refreshTimer = setInterval(async () => {
      if (document.hidden || replayTimer) return;
      try {
        const n = await lookup(liveQuery);
        if (!n || !n.found) return;
        checkedAt = Date.now();
        if (sig(n) !== liveSig) {
          liveSig = sig(n); renderResult(n, true);
          if ((n.stage || 0) >= 5) { clearInterval(clockTimer); clearInterval(refreshTimer); }
        }
        tickChecked();
      } catch (e) { /* keep the last result on screen */ }
    }, 15 * 60000);
  }

  function act(btn, r, eta) {
    const a = btn.dataset.act;
    if (a === 'copy') {
      navigator.clipboard && navigator.clipboard.writeText(r.tracking).then(() => flash(btn, S.copied));
    } else if (a === 'ics' && eta) {
      const d = (x) => x.toISOString().slice(0, 10).replace(/-/g, '');
      const end = new Date(eta.to); end.setDate(end.getDate() + 1);
      const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//VORYNEX//Tracking//EN', 'BEGIN:VEVENT',
        'UID:' + (r.tracking || Date.now()) + '@vorynexus.com', 'DTSTAMP:' + new Date().toISOString().replace(/[-:]/g, '').slice(0, 15) + 'Z',
        'DTSTART;VALUE=DATE:' + d(eta.from), 'DTEND;VALUE=DATE:' + d(end),
        'SUMMARY:' + S.calendar_title + (r.tracking ? ' (' + r.tracking + ')' : ''),
        'URL:' + location.origin + cfg.pageUrl + (r.tracking ? '?n=' + encodeURIComponent(r.tracking) : ''),
        'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
      link.download = 'vorynex-delivery.ics';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 4000);
    } else if (a === 'share') {
      // Phones: the system share sheet. Desktop: our own small menu (the Windows/macOS sheet often never shows).
      const url = location.origin + cfg.pageUrl + '?n=' + encodeURIComponent(r.tracking);
      if (navigator.share && matchMedia('(pointer: coarse)').matches) {
        navigator.share({ title: S.share_text, text: S.share_text, url }).catch(() => {});
        return;
      }
      let menu = btn.parentNode.querySelector('.vpx-tr-share');
      if (menu) { menu.remove(); btn.setAttribute('aria-expanded', 'false'); return; }
      const text = encodeURIComponent(S.share_text + ': ' + url);
      menu = document.createElement('div');
      menu.className = 'vpx-tr-share';
      menu.innerHTML =
        '<a href="https://wa.me/?text=' + text + '" target="_blank" rel="noopener">WhatsApp</a>' +
        '<a href="viber://forward?text=' + text + '">Viber</a>' +
        '<a href="mailto:?subject=' + encodeURIComponent(S.share_text) + '&body=' + encodeURIComponent(S.share_text + ':\n' + url) + '">Email</a>' +
        '<button type="button" data-copy>' + esc(S.copy) + '</button>';
      btn.after(menu);
      btn.setAttribute('aria-expanded', 'true');
      menu.querySelector('[data-copy]').addEventListener('click', (ev) => {
        navigator.clipboard && navigator.clipboard.writeText(url).then(() => { ev.target.textContent = S.link_copied; });
      });
    } else if (a === 'replay') {
      replay(btn);
    } else if (a === 'reorder') {
      // The header's add-to-cart moment (data-vpx-add) flies the photo into the cart and opens it once this lands.
      fetch(cfg.root.replace(/\/?$/, '/') + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: (r.items || []).filter((i) => i.variantId).map((i) => ({ id: i.variantId, quantity: i.qty || 1 })) })
      }).then((res) => { if (res.ok) { flash(btn, S.reorder_done); document.dispatchEvent(new CustomEvent('cart:refresh')); } });
    }
  }
  function flash(btn, text) {
    const span = btn.querySelector('span') || btn;
    const was = span.textContent;
    span.textContent = text;
    setTimeout(() => { span.textContent = was; }, 1800);
  }
  function countUp(el) {
    if (!el) return;
    const to = Number(el.dataset.count) || 0;
    const small = el.querySelector('small');
    const pre = el.dataset.prefix || '';
    if (rm || to === 0) { el.firstChild.nodeValue = pre + to; return; }
    const t0 = performance.now(), dur = 700;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      el.firstChild.nodeValue = pre + Math.round(to * (1 - Math.pow(1 - k, 3)));
      if (k < 1) requestAnimationFrame(step);
    };
    if (small) requestAnimationFrame(step);
  }

  // ---------- ETA ----------
  // DHL Kleinpaket to EU countries: 5–7 working days from hand-over (our shipping policy). Once it has reached the
  // destination country, 1–3 working days; in the delivery depot / out for delivery, today or the next working day.
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();
  const addWorkdays = (d, n) => { const x = startOfDay(d); let left = n; while (left > 0) { x.setDate(x.getDate() + 1); const w = x.getDay(); if (w !== 0 && w !== 6) left--; } return x; };
  function estimate(r, stage) {
    const ev = r.events || [];
    if (!ev.length) return null;
    const newest = new Date(ev[0].ts), oldest = new Date(ev[ev.length - 1].ts);
    let from, to;
    if (stage >= 4) { from = startOfDay(new Date()); to = addWorkdays(new Date(), 1); }
    else if (stage === 3 && ev.length >= 3) { from = addWorkdays(newest, 1); to = addWorkdays(newest, 3); }
    else { from = addWorkdays(oldest, 5); to = addWorkdays(oldest, 7); }
    const today = startOfDay(new Date());
    // The window has passed and the parcel is not out for delivery yet: no estimate rather than a false "today"
    // (the stalled notice and our email speak for it).
    if (stage < 4 && to < today) return null;
    if (from < today) from = today;
    if (to < from) to = from;
    return { from, to };
  }

  // ---------- 3D route (ported from the v3 reference: projected spline over a floor grid) ----------
  const stageEl = q('[data-vpx-track-stage]');
  const svg = q('[data-vpx-track-svg]');
  const labels = q('[data-vpx-track-labels]');
  const cube = q('[data-vpx-track-cube]');
  const tagEl = q('[data-vpx-track-tag]');
  live.observe(stageEl);
  let liveStage = 3, mx = 0.5, my = 0.5, raf = 0, cache = '', tagOverride = '';
  const NW = [[-380, -120], [-190, 20], [0, -70], [190, 100], [380, -20]];
  const PER = 140;

  function drawRoute(stage) {
    liveStage = stage;
    cache = '';
    paint();
  }
  function paint() {
    const key = mx.toFixed(3) + '|' + my.toFixed(3) + '|' + liveStage;
    if (key === cache) return;
    cache = key;
    const yaw = -0.12 + (mx - 0.5) * 0.34, pitch = 0.78 - (my - 0.5) * 0.14;
    const cyw = Math.cos(yaw), syw = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch);
    const FOC = 560, DIST = 900, CX = 320, CY = 290;
    const proj = (X, Y, Z) => { const x1 = X * cyw + Z * syw, z1 = -X * syw + Z * cyw, y2 = Y * cp + z1 * sp, z2 = -Y * sp + z1 * cp, sc = FOC / (DIST + z2); return [CX + x1 * sc, CY - y2 * sc]; };
    const f = (n) => n.toFixed(1);
    const ext = [[2 * NW[0][0] - NW[1][0], 2 * NW[0][1] - NW[1][1]]].concat(NW).concat([[2 * NW[4][0] - NW[3][0], 2 * NW[4][1] - NW[3][1]]]);
    const cr = (a, b, c, d, t) => 0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t * t + (-a + 3 * b - 3 * c + d) * t * t * t);
    const G = (u, c, w) => Math.exp(-Math.pow((u - c) / w, 2));
    const hb = (u) => 26 + 5 * G(u, 0.30, 0.05) - 9 * G(u, 0.455, 0.014) + 64 * G(u, 0.50, 0.016) - 15 * G(u, 0.545, 0.014) + 11 * G(u, 0.72, 0.06);
    const world = [];
    for (let i = 0; i < 4; i++) {
      const a = ext[i], b = ext[i + 1], c = ext[i + 2], d = ext[i + 3];
      for (let k = 0; k < PER; k++) { const t = k / PER; world.push([cr(a[0], b[0], c[0], d[0], t), cr(a[1], b[1], c[1], d[1], t), hb(t)]); }
    }
    world.push([NW[4][0], NW[4][1], 26]);
    const trace = world.map((w) => proj(w[0], w[2], w[1]));
    const floor = world.map((w) => proj(w[0], 0, w[1]));
    const traceD = 'M' + trace.map((p) => f(p[0]) + ' ' + f(p[1])).join(' L');
    const floorD = 'M' + floor.filter((p, i) => i % 4 === 0 || i === floor.length - 1).map((p) => f(p[0]) + ' ' + f(p[1])).join(' L');
    let curtainD = '';
    for (let i = 0; i < world.length; i += 7) curtainD += 'M' + f(trace[i][0]) + ' ' + f(trace[i][1]) + ' L' + f(floor[i][0]) + ' ' + f(floor[i][1]) + ' ';
    let gridD = '';
    for (let z = -320; z <= 320; z += 80) { const a = proj(-560, 0, z), b = proj(560, 0, z); gridD += 'M' + f(a[0]) + ' ' + f(a[1]) + ' L' + f(b[0]) + ' ' + f(b[1]) + ' '; }
    for (let x = -560; x <= 560; x += 80) { const a = proj(x, 0, -320), b = proj(x, 0, 320); gridD += 'M' + f(a[0]) + ' ' + f(a[1]) + ' L' + f(b[0]) + ' ' + f(b[1]) + ' '; }
    const cum = [0];
    for (let i = 1; i < trace.length; i++) cum.push(cum[i - 1] + Math.hypot(trace[i][0] - trace[i - 1][0], trace[i][1] - trace[i - 1][1]));
    const total = cum[cum.length - 1] || 1;
    let bladeD = '', ringD = '';
    const tops = NW.map((n) => {
      const fl = proj(n[0], 0, n[1]), tp = proj(n[0], 120, n[1]);
      bladeD += 'M' + f(fl[0]) + ' ' + f(fl[1]) + ' L' + f(tp[0]) + ' ' + f(tp[1]) + ' ';
      for (let a = 0; a < 16; a++) { const ang = (a / 16) * Math.PI * 2, p = proj(n[0] + Math.cos(ang) * 22, 0, n[1] + Math.sin(ang) * 22); ringD += (a ? ' L' : 'M') + f(p[0]) + ' ' + f(p[1]); }
      ringD += ' Z ';
      return tp;
    });
    const set = (name, d) => svg.querySelectorAll('[data-p="' + name + '"]').forEach((p) => p.setAttribute('d', d));
    set('grid', gridD); set('floor', floorD); set('curtain', curtainD); set('ring', ringD); set('blade', bladeD); set('trace', traceD);
    const trail = liveStage > 0 ? Math.round(cum[(liveStage - 1) * PER] / total * 1000) : 0;
    svg.querySelector('[data-trail]').style.strokeDasharray = trail + ' 1000';

    const nodes = svg.querySelector('[data-nodes]');
    let nh = '', lh = '';
    NW.forEach((n, i) => {
      const k = i + 1, p = trace[i * PER], t = tops[i];
      const st = liveStage === 0 ? 'todo' : (k < liveStage || (k === liveStage && liveStage === 5)) ? 'done' : k === liveStage ? 'now' : 'todo';
      nh += '<circle cx="' + f(p[0]) + '" cy="' + f(p[1]) + '" r="5.5" fill="' + (st === 'done' ? '#F5F5F0' : '#0A0A0B') + '" stroke="' + (st === 'todo' ? '#55555B' : st === 'now' ? '#FF6B35' : '#F5F5F0') + '" stroke-width="' + (st === 'now' ? 2 : 1.5) + '"/>';
      if (st === 'now') nh += '<circle class="vpx-tr-node-ring" cx="' + f(p[0]) + '" cy="' + f(p[1]) + '" r="5.5"/>';
      if (st !== 'now') lh += '<span class="vpx-tr-node-label' + (st === 'todo' ? '' : ' is-on') + '" style="left:' + (t[0] / 6.4).toFixed(2) + '%;top:' + (t[1] / 5.6).toFixed(2) + '%">0' + k + '</span>';
    });
    nodes.innerHTML = nh;
    labels.innerHTML = lh;
    if (liveStage > 0) {
      const t = tops[liveStage - 1], lx = t[0] / 6.4, ly = t[1] / 5.6, flip = t[0] / 640 > 0.45;
      cube.hidden = false; cube.style.left = lx + '%'; cube.style.top = 'calc(' + ly + '% - 26px)';
      tagEl.hidden = false; tagEl.textContent = tagOverride || ('0' + liveStage + ' · ' + S['step_' + liveStage]);
      tagEl.style.top = ly + '%';
      tagEl.style.left = flip ? 'calc(' + lx + '% - 26px)' : 'calc(' + lx + '% + 26px)';
      tagEl.style.transform = flip ? 'translate(-100%,-50%)' : 'translateY(-50%)';
    } else { cube.hidden = true; tagEl.hidden = true; }
  }
  if (fine && !rm) {
    stageEl.addEventListener('pointermove', (e) => {
      const b = stageEl.getBoundingClientRect();
      mx = (e.clientX - b.left) / b.width; my = (e.clientY - b.top) / b.height;
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; paint(); });
    });
    stageEl.addEventListener('pointerleave', () => { mx = 0.5; my = 0.5; if (!raf) raf = requestAnimationFrame(() => { raf = 0; paint(); }); });
  }
  drawRoute(3); // the sample route shows stage 3 until a search returns


  // ---------- the journey map: a dot-matrix Europe, Germany → the parcel's country ----------
  // Dots: Natural Earth 110m land, pre-rendered into assets/vpx-track-europe.json (one path of round-capped
  // zero-length strokes + a label point per country). The parcel sits on the arc where its stage puts it.
  let europe = null, mapState = null;
  const regionName = (iso) => { try { return new Intl.DisplayNames([lang], { type: 'region' }).of(iso); } catch (e) { return iso; } };
  const mapT = (r, stage) => [0, 0, 0.04, r.inDestination || (r.events || []).length >= 4 ? 0.82 : 0.45, 0.95, 1][stage] || 0;
  async function buildMap(el, r, stage) {
    if (!el) return;
    try { europe = europe || await (await fetch(cfg.europe)).json(); } catch (e) { el.remove(); return; }
    const O = europe.pts.DE, D = europe.pts[(r.country || 'GR').toUpperCase()] || europe.pts.GR;
    const dx = D[0] - O[0], dy = D[1] - O[1], dist = Math.hypot(dx, dy) || 1;
    // A gentle arc: bow perpendicular to the route, upwards for east–west trips, eastwards for north–south ones.
    let nx = -dy / dist, ny = dx / dist;
    if (ny > 0.3 || (Math.abs(ny) <= 0.3 && nx < 0)) { nx = -nx; ny = -ny; }
    const C = [(O[0] + D[0]) / 2 + nx * dist * 0.26, (O[1] + D[1]) / 2 + ny * dist * 0.26];
    let x0 = Math.min(O[0], D[0], C[0]) - 190, x1 = Math.max(O[0], D[0], C[0]) + 190;
    let y0 = Math.min(O[1], D[1], C[1]) - 130, y1 = Math.max(O[1], D[1], C[1]) + 130;
    const minW = (y1 - y0) * 1.5; if (x1 - x0 < minW) { const c = (x0 + x1) / 2; x0 = c - minW / 2; x1 = c + minW / 2; }
    const arc = 'M' + O.join(' ') + ' Q' + C.join(' ') + ' ' + D.join(' ');
    el.innerHTML =
      '<svg viewBox="' + [x0, y0, x1 - x0, y1 - y0].map(Math.round).join(' ') + '" role="img" aria-label="' + esc(S.map_title + ': ' + regionName('DE') + ' → ' + regionName(r.country || 'GR')) + '">' +
        '<path d="' + europe.dots + '" fill="none" stroke="#44444c" stroke-width="5.5" stroke-linecap="round"/>' +
        '<path d="' + arc + '" fill="none" stroke="#F5F5F0" stroke-opacity=".18" stroke-width="2" stroke-dasharray="4 7"/>' +
        '<path data-arc d="' + arc + '" pathLength="1000" fill="none" stroke="#FF6B35" stroke-width="3" stroke-linecap="round" stroke-dasharray="0 1000" style="filter:drop-shadow(0 0 6px rgba(255,107,53,.8))"/>' +
        '<path data-head d="' + arc + '" pathLength="1000" fill="none" stroke="#FFE3D6" stroke-width="4" stroke-linecap="round" stroke-dasharray="0 1000" opacity="0"/>' +
        '<path data-pulse class="vpx-tr-map-pulse" pathLength="100" fill="none" stroke="#FFE3D6" stroke-width="3.5" stroke-linecap="round" stroke-dasharray="12 100"/>' +
        '<circle cx="' + O[0] + '" cy="' + O[1] + '" r="7" fill="#F5F5F0"/>' +
        '<circle cx="' + D[0] + '" cy="' + D[1] + '" r="7" fill="#0A0A0B" stroke="#F5F5F0" stroke-width="2"/>' +
        '<text x="' + (O[0] + 14) + '" y="' + (O[1] - 12) + '" fill="#B4B4BA" font-size="22" font-family="var(--tr-mono)">' + esc(regionName('DE')) + '</text>' +
        '<text x="' + (D[0] + 14) + '" y="' + (D[1] + 30) + '" fill="#F5F5F0" font-size="22" font-family="var(--tr-mono)">' + esc(regionName(r.country || 'GR')) + '</text>' +
        '<g data-parcel' + (stage >= 2 && stage < 5 ? ' class="is-moving"' : '') + '><circle class="vpx-tr-map-ping" r="14" fill="none" stroke="#FF6B35" stroke-width="2"/>' +
          '<circle class="vpx-tr-map-burst" r="14" fill="none" stroke="#FF6B35" stroke-width="3"/>' +
          '<g class="vpx-tr-map-box"><polygon points="0,-13 13,-6.5 0,0 -13,-6.5" fill="#FF9A70"/><polygon points="-13,-6.5 0,0 0,14 -13,7.5" fill="#FF6B35"/>' +
          '<polygon points="13,-6.5 0,0 0,14 13,7.5" fill="#C24A1F"/><path d="M-6.5 -9.75L6.5 -3.25" stroke="#FFE3D6" stroke-width="2" stroke-opacity=".75"/></g>' +
          '<text class="vpx-tr-map-tag" x="22" y="-20">' + esc(r.gift ? S.map_here_gift : S.map_here) + '</text></g>' +
      '</svg>' +
      '<div class="vpx-tr-map__head"><span class="vpx-tr-mono vpx-tr-muted">' + esc(S.map_title) + '</span></div>' +
      '<p class="vpx-tr-map__note">' + esc(S.map_note) + '</p>';
    live.observe(el);
    mapState = { el, O, C, D, t: 0 };
    if (rm) return setMapT(mapT(r, stage), false);
    setMapT(0, false);
    // The parcel travels (and lands) when the map comes into view, so the journey is actually seen.
    const go = new IntersectionObserver((es) => {
      if (!es.some((e) => e.isIntersecting)) return;
      go.disconnect();
      if (mapState && mapState.el === el && !replayTimer) setMapT(mapT(r, stage), true);
    }, { threshold: 0.35 });
    go.observe(el);
  }
  function setMapT(target, animate) {
    const m = mapState; if (!m || !m.el.isConnected) return;
    const arcEl = m.el.querySelector('[data-arc]'), parcel = m.el.querySelector('[data-parcel]');
    const head = m.el.querySelector('[data-head]'), pulse = m.el.querySelector('[data-pulse]');
    const put = (t) => {
      const u = 1 - t, x = u * u * m.O[0] + 2 * u * t * m.C[0] + t * t * m.D[0], y = u * u * m.O[1] + 2 * u * t * m.C[1] + t * t * m.D[1];
      parcel.setAttribute('transform', 'translate(' + x.toFixed(1) + ' ' + y.toFixed(1) + ')');
      arcEl.style.strokeDasharray = Math.round(t * 1000) + ' 1000';
      // the travelled part of the arc (the same curve cut at t), for the light pulse that runs up to the parcel
      const c1 = [m.O[0] + (m.C[0] - m.O[0]) * t, m.O[1] + (m.C[1] - m.O[1]) * t];
      pulse.setAttribute('d', 'M' + m.O.join(' ') + ' Q' + c1.map((v) => v.toFixed(1)).join(' ') + ' ' + x.toFixed(1) + ' ' + y.toFixed(1));
      const h = Math.min(60, Math.round(t * 1000));
      head.style.strokeDasharray = h + ' 1000'; head.style.strokeDashoffset = String(h - Math.round(t * 1000));
      m.t = t;
    };
    const settle = () => { head.setAttribute('opacity', '0'); m.el.classList.toggle('is-transit', m.t > 0.02 && m.t < 1); };
    put(m.t);
    if (!animate) { put(target); return settle(); }
    const from = m.t, t0 = performance.now(), dur = 1400;
    m.el.classList.remove('is-transit'); parcel.classList.remove('is-landed');
    head.setAttribute('opacity', target > from ? '1' : '0');
    const step = (now) => {
      const k = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - k, 3); put(from + (target - from) * e);
      if (k < 1) return requestAnimationFrame(step);
      settle();
      // delivered: the parcel lands with a small bounce and a ring of light
      if (target >= 1 && from < 1) requestAnimationFrame(() => parcel.classList.add('is-landed'));
    };
    requestAnimationFrame(step);
  }

  // ---------- replay: the journey again, event by event, on the route and the map ----------
  let replayTimer = 0;
  function replay(btn) {
    const r = current; if (!r) return;
    const stop = () => {
      clearTimeout(replayTimer); replayTimer = 0; tagOverride = '';
      btn.setAttribute('aria-pressed', 'false'); btn.querySelector('span').textContent = S.replay;
      drawRoute(currentStage); setMapT(mapT(r, currentStage), true);
    };
    if (replayTimer) return stop();
    const seq = (r.events || []).slice().reverse();
    btn.setAttribute('aria-pressed', 'true'); btn.querySelector('span').textContent = S.replay_stop;
    stageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    let i = 0;
    const next = () => {
      if (i >= seq.length) return stop();
      const e = seq[i++], st = Math.min(5, Math.max(1, e.stage || e.k || currentStage));
      tagOverride = dayFmt.format(new Date(e.ts)) + ' · ' + e.text;
      drawRoute(st); setMapT(mapT(r, st), true);
      replayTimer = setTimeout(next, 1700);
    };
    replayTimer = setTimeout(next, 350);
  }

  // ---------- scroll story: four stills crossfade with a slow living zoom ----------
  const story = q('[data-vpx-track-story]');
  if (story) live.observe(story);
  if (story && !rm) {
    const slides = [...story.querySelectorAll('.vpx-tr-story__slide')];
    const imgs = slides.map((s) => s.querySelector('img'));
    const caps = [...story.querySelectorAll('.vpx-tr-cap')];
    const rail = story.querySelector('[data-vpx-track-rail]');
    const N = slides.length;
    // Where the LED line sits in each still (fraction of the image height); the tall burst on portrait screens differs.
    const LINE = [0.50, 0.47, 0.93, 0.497];
    const glow = story.querySelector('[data-vpx-track-glow]');
    glow.style.top = '0px';
    const stageBox = story.querySelector('.vpx-tr-story__stage');
    let onScreen = false, sRaf = 0, lastP = -1, lineY = innerHeight * 0.55, glowBase = 0.6;
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const ease = (t) => t * t * (3 - 2 * t);
    const frame = () => {
      sRaf = 0;
      const r = story.getBoundingClientRect();
      const travel = Math.max(1, r.height - innerHeight);
      const p = clamp(-r.top / travel);
      if (Math.abs(p - lastP) < 0.0005) return;
      lastP = p;
      const pos = p * (N - 1);                     // 0 … 3 across the scene
      slides.forEach((s, i) => {
        const d = Math.abs(pos - i);
        const o = i === 0 && pos < 0 ? 1 : ease(clamp(1 - (d - 0.25) / 0.5));
        s.style.opacity = o.toFixed(3);
        const local = clamp((pos - i + 1) / 2);     // 0 → 1 while this slide is around
        const k = 1.06 - 0.05 * local;
        imgs[i].style.transform = 'scale(' + k.toFixed(4) + ') translate3d(' + ((0.5 - local) * 1.2).toFixed(2) + '%,0,0)';
        slides[i]._k = k; slides[i]._o = o;
        const c = caps[i];
        if (c) { const co = ease(clamp(1 - (d - 0.12) / 0.3)); c.style.opacity = co.toFixed(3); c.style.transform = 'translateY(' + ((1 - co) * 16).toFixed(1) + 'px)'; }
      });
      rail.style.transform = 'scaleY(' + p.toFixed(4) + ')';
      // The breathing light follows the LED line of whichever stills are showing.
      const box = stageBox.getBoundingClientRect();
      let ys = 0, ws = 0;
      slides.forEach((sl, i) => {
        if (!sl._o) return;
        const tall = (imgs[i].currentSrc || '').includes('burst-tall');
        ys += coverLineY(imgs[i], box, tall ? 0.605 : LINE[i], sl._k) * sl._o; ws += sl._o;
      });
      // transform, not top: moving it with top counted as a layout shift on every scroll step (CLS)
      if (ws) { lineY = ys / ws; glow.style.transform = 'translateY(' + lineY.toFixed(1) + 'px)'; }
      glowBase = Math.min(1, ws) * (slides[2]._o > 0.5 ? 0.35 : 0.6);
    };
    const dustCanvas = story.querySelector('[data-vpx-track-dust]');
    makeDust(dustCanvas, {
      line: () => lineY,
      onFrame: (t, b) => { glow.style.opacity = (glowBase * (0.72 + 0.28 * Math.sin(t * 1.4)) * (1 + 0.5 * Math.min(1.5, b))).toFixed(3); }
    });
    const tick = () => { if (onScreen && !sRaf) sRaf = requestAnimationFrame(frame); };
    new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; if (onScreen) tick(); }).observe(story);
    addEventListener('scroll', tick, { passive: true });
    addEventListener('resize', () => { lastP = -1; tick(); });
  }

  // ---------- deep link: ?n=LF…DE searches straight away ----------
  if (forcedStage && params.get('o')) search({ mode: 'order', order: '#' + params.get('o').replace(/^#/, ''), email: 'demo@example.com' });
  const n = params.get('n');
  if (n) { setMode('track'); form.track.value = n; search({ mode: 'track', tracking: n.replace(/\s+/g, '').toUpperCase() }); }
}
